/**
 * Bidirectional mapping between settings modal routes (the `SettingsItem.href`
 * keys used by the IA, e.g. `/settings/team/evidence`) and URL slugs
 * (`team-evidence`) used in the `?settings=` query param.
 *
 * Slugs are deliberately flat — no slashes — so they don't need URL-encoding
 * and play nicely with analytics tooling that splits on `/`.
 *
 * Keep this in sync with the `SCREENS` / `screenImporters` maps in
 * `src/features/settings/components/SettingsModal/settingsScreenLoaders.ts`.
 * Adding a new section means adding both an entry here AND a screenImporters
 * entry. Drift between the two is caught by the parity test in
 * `settingsScreenLoaders.test.ts` (`'covers exactly the routes declared in
 * ROUTE_TO_SLUG (drift guard)'`).
 */

export const SETTINGS_QUERY_PARAM = 'settings';

/**
 * The literal source of both maps below.
 *
 * `as const` so the slug vocabulary is available as a TYPE — composition keys
 * its settings-screen host registry by it, and a key that is not a real slug
 * has to be a compile error there rather than a host that silently never runs.
 * The two exported maps keep their widened `Record<string, string>` type, so
 * every existing call site indexing them with a plain `string` is unaffected.
 */
const SETTINGS_ROUTE_SLUGS = {
  '/settings/account': 'account',
  '/settings/billing': 'billing',
  '/settings/memory': 'memory',
  '/settings/display-controls': 'display-controls',
  '/settings/data-management': 'data-management',
  '/settings/defaults': 'defaults',
  '/settings/dictate': 'dictate',
  '/settings/notifications': 'notifications',
  '/settings/evidence': 'evidence',
  '/settings/comms': 'comms',
  '/settings/comms-data-management': 'comms-data-management',
  '/settings/summaries': 'summaries',
  '/settings/blocked-numbers': 'blocked-numbers',
  '/settings/comms-integrations': 'comms-integrations',
  '/settings/prechart': 'prechart',
  '/settings/heidi-labs': 'heidi-labs',
  '/settings/team': 'team',
  '/settings/organization': 'organization',
  '/settings/members': 'members',
  '/settings/templates': 'templates',
  '/settings/team/evidence': 'team-evidence',
  '/settings/usage': 'usage',
  '/settings/api-status': 'api-status',
  '/settings/integrations': 'integrations',
  '/settings/integrations/details': 'integration-details',
  '/settings/integration-agent': 'integration-agent',
  '/settings/coding': 'coding',
  '/settings/inbox': 'inbox',
  '/settings/nudge-settings': 'nudge-settings',
  '/settings/cloud-computer': 'cloud-computer',
  '/settings/version': 'version',
  '/settings/computer-use': 'computer-use',
  '/settings/remote': 'remote',
} as const;

/** Every slug the `?settings=` param and a settings contribution id may name. */
export type SettingsScreenSlug =
  (typeof SETTINGS_ROUTE_SLUGS)[keyof typeof SETTINGS_ROUTE_SLUGS];

export const ROUTE_TO_SLUG: Record<string, string> = SETTINGS_ROUTE_SLUGS;

export const SLUG_TO_ROUTE: Record<string, string> = Object.fromEntries(
  Object.entries(ROUTE_TO_SLUG).map(([route, slug]) => [slug, route])
);

export const routeToSlug = (route: string): string | undefined => ROUTE_TO_SLUG[route];

export const slugToRoute = (slug: string): string | undefined => SLUG_TO_ROUTE[slug];
